import { z } from "zod";
import { AgentSurface } from "./agents.ts";
import { Id, IsoDate } from "./common.ts";
import { Proposal } from "./copilot.ts";
import { DashboardFilters, Filter, QueryScope, WidgetQuery, type DashboardFilters as DashboardFiltersT, type Filter as FilterT, type WidgetQuery as WidgetQueryT } from "./dashboards.ts";
import { FlagKey, RuleInput, type ConditionT, type RuleInput as RuleInputT } from "./domain.ts";
import type { RouteDef } from "./routes.ts";

/**
 * Dashboards → actions (ALP-83). A chart segment (donut slice, bar, point), a table row/selection or a comments widget
 * describes a set of items as WidgetQuery filters (+ the dashboard filters + the clicked segment). From there:
 * - "Create rule from this": `ruleFromWidget` translates the filters into rule conditions (pure, below) and opens the
 *   rule builder pre-filled; filters a rule can't express are listed in `unmapped`.
 * - "Apply action to these items": `widgetItems` resolves the matching items (count + capped ids), the user picks an
 *   action, `widgetItemsPropose` stores a Copilot-style proposal (review popup → execute as a job → undo where
 *   reversible, audited).
 */

/** The set of items a widget (or one of its segments) shows. */
export const WidgetSelector = z.object({
  query: WidgetQuery,
  filters: DashboardFilters.optional(),
  scope: QueryScope.optional(),
  /** The clicked segment / selected rows as filters (e.g. sentiment = negative, post_id in [...]). */
  segment: z.array(Filter).max(10).default([]),
  /** Table/list selection: exactly these ids (still re-checked against the filters + org). */
  ids: z.array(Id).max(500).optional(),
});
export const ITEM_CAP = 500;
export const WidgetItemsRequest = WidgetSelector.extend({ limit: z.number().int().min(1).max(ITEM_CAP).default(200) });
export const WidgetItemsResult = z.object({
  entity: z.enum(["comment", "conversation"]),
  /** All matching items (may exceed the cap). */
  total: z.number().int(),
  /** The first `limit` matching ids (newest first): what an action applies to. */
  ids: z.array(Id),
  capped: z.boolean(),
  sample: z.array(z.object({ id: Id, text: z.string(), author: z.string().nullable(), at: IsoDate })).max(5),
});
export const ItemAction = z.discriminatedUnion("type", [
  z.object({ type: z.literal("flag"), add: z.array(FlagKey).max(10).default([]), remove: z.array(FlagKey).max(10).default([]) }),
  z.object({ type: z.literal("hide") }),
  z.object({ type: z.literal("unhide") }),
  z.object({ type: z.literal("archive") }),
  z.object({ type: z.literal("unarchive") }),
  z.object({ type: z.literal("reply_with_agent"), agentId: Id, surface: AgentSurface.optional() }),
  z.object({ type: z.literal("assign"), assigneeId: Id }),
]);
export const WidgetProposeRequest = WidgetSelector.extend({
  action: ItemAction,
  limit: z.number().int().min(1).max(ITEM_CAP).default(200),
  title: z.string().max(140).optional(),
});
export const RuleFromWidgetRequest = WidgetSelector.extend({ name: z.string().max(120).optional() });
export const RuleFromWidgetResult = z.object({
  /** A RuleInput to open in the builder (not saved). */
  rule: RuleInput,
  /** Human-readable filters that became conditions or scope ("sentiment = negative", "post = …"). */
  mapped: z.array(z.string()),
  /** Filters a rule can't express (time range, status, numbers…): shown as a note in the builder. */
  unmapped: z.array(z.string()),
});

// ---------------------------------------------------------------- pure mapping (API + web mock)
export interface RuleDraftFromWidget {
  rule: RuleInputT;
  mapped: string[];
  unmapped: string[];
  /** Post ids the filters pin the rule to: the API turns exactly one post with one publication into `scope.publicationId`. */
  postIds: string[];
}

const TRIGGER_OF: Partial<Record<WidgetQueryT["dataset"], RuleInputT["trigger"]>> = { comments: "comment.created", messages: "message.received", conversations: "message.received", automation_runs: "comment.created" };
const asList = (v: FilterT["value"]): string[] => (Array.isArray(v) ? v.map(String) : v === null || v === undefined ? [] : [String(v)]);
const describe = (f: FilterT) => `${f.field} ${f.op} ${Array.isArray(f.value) ? f.value.join(", ") : String(f.value)}`;

/** Merge the widget's own filters, the dashboard filter bar and the clicked segment into one filter list. */
export function selectorFilters(input: { query: WidgetQueryT; filters?: DashboardFiltersT | undefined; segment?: FilterT[] | undefined }): FilterT[] {
  const out: FilterT[] = [...input.query.filters];
  const df = input.filters;
  if (df) {
    if (df.accounts?.length) out.push({ field: "account_id", op: "in", value: df.accounts });
    if (df.sentiment?.length) out.push({ field: "sentiment", op: "in", value: df.sentiment });
    if (df.intent?.length) out.push({ field: "intent", op: "in", value: df.intent });
    if (df.tags?.length) out.push({ field: "tag", op: "in", value: df.tags });
    if (df.posts?.length) out.push({ field: "post_id", op: "in", value: df.posts });
    if (df.channels?.length) out.push({ field: "channel", op: "in", value: df.channels });
    out.push(...(df.extra ?? []));
  }
  out.push(...(input.segment ?? []));
  return out;
}

/**
 * Widget filters → rule conditions. Each filter maps to one leaf (neq / nin wrap it in `not`); several filters are ANDed.
 * `by_account = false` is implied (rules never run on the account's own comments). Unsupported fields go to `unmapped`.
 */
export function ruleFromWidget(input: { query: WidgetQueryT; filters?: DashboardFiltersT | undefined; segment?: FilterT[] | undefined; scope?: { scope: string; ref?: string | null | undefined } | undefined; name?: string | undefined }): RuleDraftFromWidget {
  const trigger = TRIGGER_OF[input.query.dataset];
  const leaves: ConditionT[] = [];
  const mapped: string[] = [];
  const unmapped: string[] = [];
  const postIds = new Set<string>();
  let accountId: string | null = null;
  const all = selectorFilters(input);
  if (input.scope?.scope === "post" && input.scope.ref) all.push({ field: "post_id", op: "eq", value: input.scope.ref });
  if (input.scope?.scope === "account" && input.scope.ref) all.push({ field: "account_id", op: "eq", value: input.scope.ref });
  const negate = (c: ConditionT, op: FilterT["op"]): ConditionT => (op === "neq" || op === "nin" ? { op: "not", of: c } : c);
  for (const f of all) {
    const vals = asList(f.value);
    const listOp = f.op === "eq" || f.op === "in" || f.op === "neq" || f.op === "nin";
    switch (f.field) {
      case "sentiment":
        if (listOp && vals.length) { leaves.push(negate({ op: "sentiment", in: vals }, f.op)); mapped.push(describe(f)); continue; }
        break;
      case "intent":
        if (listOp && vals.length) { leaves.push(negate({ op: "intent", in: vals, minConfidence: 0.7 }, f.op)); mapped.push(describe(f)); continue; }
        break;
      case "tag":
        if (listOp && vals.length) { leaves.push(negate({ op: "has_tag", in: vals }, f.op)); mapped.push(describe(f)); continue; }
        break;
      case "language":
        if (listOp && vals.length) { leaves.push(negate({ op: "language", in: vals }, f.op)); mapped.push(describe(f)); continue; }
        break;
      case "is_reply":
        if (f.op === "eq" && typeof f.value === "boolean") { leaves.push(f.value ? { op: "is_reply" } : { op: "not", of: { op: "is_reply" } }); mapped.push(describe(f)); continue; }
        break;
      case "text":
        if (f.op === "contains" && typeof f.value === "string" && f.value.trim()) { leaves.push({ op: "keyword", words: [f.value.trim()], mode: "contains" }); mapped.push(describe(f)); continue; }
        if (f.op === "semantic" && typeof f.value === "string" && f.value.trim()) { leaves.push({ op: "ai_prompt", describe: f.value.trim() }); mapped.push(describe(f)); continue; }
        break;
      case "by_account":
        if (f.op === "eq" && f.value === false) continue; // implied
        break;
      case "post_id":
        if ((f.op === "eq" || f.op === "in") && vals.length) { vals.forEach((v) => postIds.add(v)); mapped.push(describe(f)); continue; }
        break;
      case "account_id":
        if ((f.op === "eq" || f.op === "in") && vals.length === 1) { accountId = vals[0]!; mapped.push(describe(f)); continue; }
        break;
    }
    unmapped.push(describe(f));
  }
  if (!trigger) unmapped.unshift(`dataset ${input.query.dataset}`);
  const conditions: ConditionT = leaves.length === 1 ? leaves[0]! : { op: "all", of: leaves };
  const label = mapped.length ? mapped.slice(0, 2).join(" · ") : input.query.dataset;
  return {
    rule: {
      name: (input.name ?? `From dashboard: ${label}`).slice(0, 120),
      icon: "📊",
      color: "#4f3fe0",
      scope: { accountId, publicationId: null },
      trigger: trigger ?? "comment.created",
      conditions,
      actions: [{ type: "flag", flags: ["follow_up"], target: "item" }],
      mode: "approve",
      limits: { perHour: 30, quietHours: null },
      priority: 50,
      isActive: false,
    },
    mapped,
    unmapped,
    postIds: [...postIds],
  };
}

export type WidgetSelectorT = z.input<typeof WidgetSelector>;
export type WidgetItemsResultT = z.infer<typeof WidgetItemsResult>;
export type ItemActionT = z.infer<typeof ItemAction>;
export type RuleFromWidgetResultT = z.infer<typeof RuleFromWidgetResult>;

// ---------------------------------------------------------------- routes (spread into `routes`)
const route = <P extends z.ZodTypeAny, Q extends z.ZodTypeAny, B extends z.ZodTypeAny, R extends z.ZodTypeAny>(d: RouteDef<P, Q, B, R>) => d;
const OrgP = z.object({ orgId: z.string().uuid() });

export const widgetActionRoutes = {
  widgetItems: route({ method: "POST", path: "/v1/orgs/:orgId/analytics/items", params: OrgP, body: WidgetItemsRequest, response: WidgetItemsResult, summary: "Resolve the comments / conversations a widget or segment shows (count + capped ids; viewer allowed)" }),
  widgetItemsPropose: route({ method: "POST", path: "/v1/orgs/:orgId/analytics/items/propose", params: OrgP, body: WidgetProposeRequest, response: Proposal, status: 201, summary: "Propose an action on those items (flag/tag, hide, archive, reply with agent, assign): review, then execute as a job" }),
  ruleFromWidget: route({ method: "POST", path: "/v1/orgs/:orgId/rules/from-widget", params: OrgP, body: RuleFromWidgetRequest, response: RuleFromWidgetResult, summary: "Translate a widget's filters + the clicked segment into a rule draft (not saved)" }),
} as const;
